import React from 'react'
import useSenateControl from './useSenateControl'

/**
 * The banner's ground: a near-black field split between the parties. The
 * Democratic blue slab holds the left, the Republican red wash the right,
 * and the skewed seam between them parks at the Senate market's split —
 * the background itself is the quote. A star-field texture sits over both.
 *
 * `compact` is the home strip and the feed card: a short frame, so the
 * party wash stays visible instead of dissolving into the page-top fade
 * the map uses under the header.
 */
const MidtermBackdrop = ({ className = '', compact = false }) => {
  const { share } = useSenateControl()

  /* Clamped so a runaway quote never pushes the seam under the copy's edges. */
  const seam = Math.min(68, Math.max(32, share))

  const dots = compact ? '22px 22px' : '26px 26px'

  return (
    <div
      className={`pointer-events-none absolute inset-x-0 overflow-hidden bg-surface-2 [html[data-theme='dark']_&]:bg-[#08070f] ${
        compact ? 'inset-y-0' : '-top-48 bottom-0'
      } ${className}`}
      aria-hidden="true">
      <div
        className={`absolute inset-0 ${
          compact
            ? 'bg-[linear-gradient(270deg,rgba(214,38,66,0.2)_0%,rgba(214,38,66,0.07)_44%,transparent_66%)] [html[data-theme=\'dark\']_&]:bg-[linear-gradient(270deg,rgba(214,38,66,0.42)_0%,rgba(214,38,66,0.14)_44%,transparent_66%)]'
            : 'bg-[linear-gradient(270deg,rgba(214,38,66,0.12)_0%,rgba(214,38,66,0.04)_38%,transparent_62%)] [html[data-theme=\'dark\']_&]:bg-[linear-gradient(270deg,rgba(214,38,66,0.2)_0%,rgba(214,38,66,0.06)_38%,transparent_62%)]'
        }`}
      />
      <div
        className={`absolute -top-32 -bottom-[12%] -left-28 border-r border-Neutral-3 transition-[width] duration-700 [html[data-theme='dark']_&]:border-white/10 ${
          compact
            ? 'bg-[linear-gradient(90deg,rgba(47,60,220,0.22)_0%,rgba(47,60,220,0.08)_68%,rgba(47,60,220,0.02)_100%)] [html[data-theme=\'dark\']_&]:bg-[linear-gradient(90deg,rgba(47,60,220,0.48)_0%,rgba(47,60,220,0.18)_68%,rgba(47,60,220,0.04)_100%)]'
            : 'bg-[linear-gradient(90deg,rgba(47,60,220,0.14)_0%,rgba(47,60,220,0.05)_70%,rgba(47,60,220,0.01)_100%)] [html[data-theme=\'dark\']_&]:bg-[linear-gradient(90deg,rgba(47,60,220,0.24)_0%,rgba(47,60,220,0.08)_70%,rgba(47,60,220,0.02)_100%)]'
        }`}
        style={{ width: `calc(${seam}% + 7rem)`, transform: 'skewX(-8deg)' }}
      />
      <div
        className="absolute inset-y-[-12%] w-10 bg-Neutral-5/[0.04] blur-md transition-[left] duration-700 [html[data-theme='dark']_&]:bg-white/[0.03]"
        style={{ left: `${seam}%`, transform: 'translateX(-50%) skewX(-8deg)' }}
      />
      <div
        className={`absolute inset-0 [html[data-theme='dark']_&]:hidden ${compact ? 'opacity-70' : 'opacity-50'}`}
        style={{
          backgroundImage: 'radial-gradient(rgba(15,40,52,0.1) 1px, transparent 1.4px)',
          backgroundSize: dots,
        }}
      />
      <div
        className={`absolute inset-0 hidden [html[data-theme='dark']_&]:block ${compact ? 'opacity-70' : 'opacity-50'}`}
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.1) 1px, transparent 1.4px)',
          backgroundSize: dots,
        }}
      />
      {compact ? (
        <>
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.2)_0%,transparent_22%,transparent_74%,rgba(245,246,250,0.4)_100%)] [html[data-theme='dark']_&]:hidden" />
          <div className="absolute inset-0 hidden bg-[linear-gradient(180deg,rgba(8,7,15,0.16)_0%,transparent_22%,transparent_74%,rgba(0,0,0,0.36)_100%)] [html[data-theme='dark']_&]:block" />
        </>
      ) : (
        <>
          <div className="absolute inset-0 bg-[linear-gradient(180deg,var(--color-surface-1)_0%,rgba(245,246,250,0.92)_14%,rgba(245,246,250,0.55)_30%,transparent_52%,transparent_70%,var(--color-surface-1)_100%)] [html[data-theme='dark']_&]:hidden" />
          <div className="absolute inset-0 hidden bg-[linear-gradient(180deg,#08070f_0%,rgba(8,7,15,0.94)_14%,rgba(8,7,15,0.62)_30%,rgba(8,7,15,0.22)_46%,transparent_62%,transparent_70%,rgba(0,0,0,0.38)_100%)] [html[data-theme='dark']_&]:block" />
        </>
      )}
    </div>
  )
}

export default MidtermBackdrop
